import { useEffect, useState } from "react";
import { Users, MapPin, Trophy, Filter } from "lucide-react";
import api from "@/lib/api";
import { BadgePill } from "@/components/Badges";

const RankNo = ({ n }) => (
  <span
    className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center font-display font-bold text-sm ${
      n === 1 ? "bg-talanta-gold text-talanta-green" : n === 2 ? "bg-slate-300 text-slate-800" : n === 3 ? "bg-[#B87333] text-white" : "bg-talanta-cream text-talanta-charcoal/60"
    }`}
  >
    {n}
  </span>
);

export const InviteLeaderboard = ({ onShare }) => {
  const [data, setData] = useState(null);
  const [county, setCounty] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/leaderboard", { params: county ? { county } : {} })
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData((d) => d || { top: [], counties: [], totals: { invites: 0, inviters: 0 }, badges: [] }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [county]);

  const chips = (data?.counties || []).slice(0, 6).map((c) => c.county);

  return (
    <section id="leaderboard" data-testid="invite-leaderboard" className="relative bg-talanta-card py-20 sm:py-24 overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-talanta-gold/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-xl">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-talanta-terracotta">Invite leaderboard</span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-talanta-green">Who's growing the talent economy?</h2>
            <p className="mt-3 text-talanta-charcoal/70">
              Every friend who joins through your ID card counts. Earn Bronze, Silver, Gold and Champion badges — and put your county on top.
            </p>
          </div>
          <div className="flex gap-6" data-testid="leaderboard-totals">
            <div>
              <div className="font-display font-extrabold text-3xl text-talanta-green">{data?.totals?.invites ?? "—"}</div>
              <div className="text-xs uppercase tracking-wider text-talanta-charcoal/50 mt-1">Friends invited</div>
            </div>
            <div>
              <div className="font-display font-extrabold text-3xl text-talanta-green">{data?.totals?.inviters ?? "—"}</div>
              <div className="text-xs uppercase tracking-wider text-talanta-charcoal/50 mt-1">Active inviters</div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="leaderboard-county-filter">
          <Filter className="h-4 w-4 text-talanta-charcoal/40" />
          {["", ...chips].map((c) => (
            <button
              key={c || "all"}
              data-testid={`leaderboard-chip-${(c || "national").toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => setCounty(c)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                county === c ? "bg-talanta-green text-white border-talanta-green" : "bg-white text-talanta-charcoal/70 border-talanta-green/15 hover:border-talanta-green/40"
              }`}
            >
              {c || "National"}
            </button>
          ))}
        </div>

        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 rounded-2xl bg-white border border-talanta-green/10 overflow-hidden" data-testid="leaderboard-top">
            <div className="px-5 py-4 border-b border-talanta-green/10 flex items-center gap-2 text-xs uppercase tracking-wider text-talanta-charcoal/50">
              <Trophy className="h-4 w-4 text-talanta-gold" /> Top inviters {county ? `· ${county}` : "· Kenya"}
            </div>
            {loading && !data ? (
              <div className="p-8 text-center text-sm text-talanta-charcoal/50">Loading standings…</div>
            ) : data?.top?.length ? (
              <ol>
                {data.top.map((r) => (
                  <li key={r.talanta_id} data-testid={`leaderboard-row-${r.rank}`} className="flex items-center gap-3 px-5 py-3 border-b border-talanta-green/5 last:border-0 hover:bg-talanta-cream/60">
                    <RankNo n={r.rank} />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-semibold text-talanta-green">{r.talanta_id}</div>
                      <div className="text-xs text-talanta-charcoal/50 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {r.county || "County not set"}
                      </div>
                    </div>
                    <BadgePill badge={r.badge} />
                    <div className="text-right w-16">
                      <div className="font-display font-bold text-talanta-green">{r.invites}</div>
                      <div className="text-[10px] uppercase tracking-wider text-talanta-charcoal/40">invites</div>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="p-8 text-center" data-testid="leaderboard-empty">
                <Users className="h-8 w-8 mx-auto text-talanta-charcoal/30" />
                <p className="mt-3 text-sm text-talanta-charcoal/60">No inviters yet{county ? ` in ${county}` : ""}. Be the first — share your ID card.</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="rounded-2xl bg-white border border-talanta-green/10 overflow-hidden" data-testid="leaderboard-counties">
              <div className="px-5 py-4 border-b border-talanta-green/10 flex items-center gap-2 text-xs uppercase tracking-wider text-talanta-charcoal/50">
                <MapPin className="h-4 w-4 text-talanta-terracotta" /> County standings
              </div>
              {data?.counties?.length ? (
                <ol className="p-2">
                  {data.counties.slice(0, 8).map((c) => {
                    const max = data.counties[0].invites || 1;
                    return (
                      <li key={c.county} className="px-3 py-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-talanta-charcoal">{c.rank}. {c.county}</span>
                          <span className="text-talanta-charcoal/60">{c.invites} · {c.inviters} inviter{c.inviters === 1 ? "" : "s"}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-talanta-cream overflow-hidden">
                          <div className="h-full rounded-full bg-talanta-terracotta" style={{ width: `${Math.max(6, (c.invites / max) * 100)}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="p-6 text-center text-sm text-talanta-charcoal/50">Counties appear here once members with a county start inviting.</div>
              )}
            </div>

            <div className="rounded-2xl bg-talanta-green text-white p-5" data-testid="leaderboard-badges">
              <div className="text-xs uppercase tracking-wider text-talanta-gold font-semibold">Badge tiers</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[...(data?.badges || [])].reverse().map((b) => (
                  <div key={b.id} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex items-center justify-between">
                    <BadgePill badge={b} dark />
                    <span className="text-xs text-white/60">{b.min}+</span>
                  </div>
                ))}
              </div>
              <button
                data-testid="leaderboard-share-cta"
                onClick={onShare}
                className="mt-4 w-full rounded-full bg-talanta-red hover:bg-talanta-red/90 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
              >
                Share my ID &amp; start climbing
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
